import { IAddOnPlans, IFilters } from "@tm/api-utils";
import { getAnalytics } from "@tm/insurance/analytics";
import { trackComponentView, trackEvent } from "@tm/insurance/analytics/xdm";
import { checkCheckoutEligibility } from "@tm/insurance/api";
import {
  getCookieByName,
  getFormFieldErrorMsg,
  getPartnerOnClientSide,
  getTravDetailsFromMemory,
  setTravDetailsInMemory,
} from "@tm/insurance/utils";
import {
  constants,
  GENDER_VALUES,
  PARTNER,
  RESPONSE_STATUS,
} from "@tm/isomorphic-utils";
import { ImageComponent, MoreBenefits } from "@tm/static";
import { usePlanType } from "@tm/ui";
import { useBreakpointV2 } from "@tm/ui-widgets";
import { ClientError } from "@tm/utility";
import { storePageTransitionStart } from "@tm/utility/analytics";
import { theme } from "@tm/utility/global";
import { errorTrackEvent } from "libs/utility/src/lib/analytics/XDMAnalytics/xdmEvents";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  FieldArrayWithId,
  FormProvider,
  SubmitErrorHandler,
  SubmitHandler,
  useFieldArray,
  useForm,
  useWatch,
} from "react-hook-form";
import { FormattedText, H5Tag, ParaTag, SectionDividers } from "..";
import WebToast from "../Toast/WebToast";
import {
  AddButtonWrap,
  AddTravellerDetailStyle,
  AddTravellerHd,
  AddViewButton,
  ArrowIcon,
  CheckoutLoader,
  CheckoutLoaderBackdrop,
  CheckoutLoaderText,
  ViewButtonWrap,
  WebToastWrap,
} from "./AddTravellerDetail.style";
import TravellerCommonComp from "./TravellerCommonComp";
import TravellerDetailsV2 from "./TravellerDetailsV2";
import {
  IAddTravellerDetailProps,
  IGender,
  ISecondaryContactV2,
  ITravellerDetailsFormV2,
} from "./type";

interface Traveller {
  id: string;
  name: string;
  dob: string;
  gender: string;
  passport: string;
  isSelected: boolean;
  isEdited: boolean;
  isSavedTraveller: boolean;
}
interface TravellerError {
  index: number;
  errorType: string;
}
const analytics = getAnalytics();

const AddTravellerDetails = ({
  title,
  info,
  data,
  countryData,
  travellerCount,
  benefitsData,
  benefitsTitle,
  showBenefits,
  handleCountryCodeModal,
  handleViewBenefits,
  setAddOnProductDetails,
  addOnProductDetails,
  setActiveModal,
  isLoading: externalIsLoading,
  checkoutError,
  handleTravellerPayment,
  onFormDataChange,
  firstErrorIndex,
  setFirstErrorIndex,
  getSelectedCountry,
  onSetError,
}: IAddTravellerDetailProps) => {
  const {
    primaryContact,
    secondaryContact,
    upcomingTravellerDetails,
    savedTravellers,
    note,
    concent,
    totalAmt,
    widgets,
    isDomesticBooking,
    addOns,
    experiment,
    isSavedTravellerPokusEnabled = false,
    planInfo,
    gstInfo,
  } = data || {};
  const { addOnPlans, filters } = addOns || {};
  const { dob, name, gender, passport, nomineeName } =
    secondaryContact?.fields || {};
  const { formErrorToast, addErrorToast } = constants;
  const device = useBreakpointV2();
  const travellerDataFromStore = getTravDetailsFromMemory();
  const { travellerDetails, primaryContactDetails, gstInfoDetails } =
    travellerDataFromStore || {};
  const partner = getPartnerOnClientSide();

  const travellerRef = useRef<HTMLInputElement>(null);
  const travellerRefMap = useRef<{ [key: number]: HTMLDivElement | null }>({});

  const [isLoading, setIsLoading] = useState(false);
  const [addTrav, setAddTrav] = useState(-1);
  const [quoteId, setQuoteId] = useState<string | null>(null);
  const [isValidForm, setIsValidForm] = useState<boolean>(false);
  const [isSelectedError, setIsSelectedError] = useState<boolean>(false);
  const [isFieldError, setIsFieldError] = useState<boolean>(false);
  const [showOfferToast, setShowOfferToast] = useState<boolean>(true);
  const [addOnFilters, setAddOnFilters] = useState<any>(filters);
  const [addOnPlansData, setAddOnPlansData] = useState<any>(addOnPlans);
  const [isEligibilityLoading, setIsEligibilityLoading] =
    useState<boolean>(false);
  const [showCheckoutLoginLoaderText, setShowCheckoutLoginLoaderText] =
    useState<string>("");
  const primaryContactRef = useRef<HTMLInputElement>(null);
  const gstInfoRef = useRef<HTMLInputElement>(null);
  const tncRef = useRef<HTMLInputElement>(null);
  const { type } = usePlanType();

  useEffect(() => {
    setShowCheckoutLoginLoaderText("");
    if (sessionStorage.getItem("formData") && getCookieByName("mmt-auth")) {
      setShowCheckoutLoginLoaderText("Redirecting you to payment page");
      const formData = JSON.parse(sessionStorage.getItem("formData") || "{}");
      replace(formData);
      sessionStorage.removeItem("formData");
      handleTravellerPayment?.(formData);
    }

    if (isSavedTravellerPokusEnabled) {
      analytics?.trackTravDetailsEvents(
        { actionType: "viewAll_shown" },
        "pageView",
        type
      );
      trackComponentView({
        name: "viewAll_shown",
        type: "button",
      });
    }

    if (planInfo?.quoteId) {
      setQuoteId(planInfo.quoteId);
    } else {
      const queryParams = new URLSearchParams(window.location.search);
      const quoteId = queryParams.get("quoteId");
      setQuoteId(quoteId);
    }
    setTimeout(() => {
      setShowOfferToast(false);
    }, 3000);
  }, []);

  const methods = useForm<ITravellerDetailsFormV2>({
    defaultValues: {
      travellerData: Array.from({
        length: (savedTravellers?.length ?? 0) + travellerCount,
      }).map(() => ({
        name: "",
        dob: "",
        gender: "",
        passport: "",
        nomineeName: "",
        isSelected: false,
      })),
      primaryContactData: {
        email: "",
        mobile: "",
      },
      gstInfoDetails: {
        gstNumber: "",
        companyDetails: "",
      },
      tncs: Array.from({ length: concent?.tnc?.length ?? 0 }).map(() => true),
    },
    mode: "onTouched",
  });

  const {
    control,
    getValues,
    setValue,
    getFieldState,
    setError,
    handleSubmit,
    trigger,
  } = methods;

  const formValues = getValues();

  const travellerData = useWatch({
    control,
    name: "travellerData", // unique name for field array
  });

  const {
    fields: travellers,
    prepend,
    replace,
  } = useFieldArray({
    control,
    name: "travellerData", // unique name for field array
  });

  const selectedCount = useMemo(
    () =>
      travellerData?.filter((traveller) => traveller.isSelected)?.length ?? 0,
    [travellerData]
  );

  const [showAllTravellers, setShowAllTravellers] = useState<boolean>(false);

  useMemo(() => {
    if (selectedCount === travellerCount) {
      let isValid = true;
      travellerData?.map((data: ISecondaryContactV2) => {
        if (data.isSelected) {
          const validName = name ? (data.name ? true : false) : true;
          const validDob = dob ? (data.dob ? true : false) : true;
          const validPassport = dob ? (data.passport ? true : false) : true;
          const validGender = gender ? (data?.gender ? true : false) : true;
          const validNomineeName = nomineeName
            ? data?.nomineeName
              ? true
              : false
            : true;
          if (
            !(
              validDob &&
              validGender &&
              validName &&
              validPassport &&
              validNomineeName
            )
          ) {
            isValid = false;
          }
        }
      });
      setIsValidForm(isValid);
    } else {
      setIsValidForm(false);
    }
  }, [travellerData]);

  // To add saved travellers and upcoming travellers in memory
  useEffect(() => {
    let travellerDetail: any = [];
    const queryParams = new URLSearchParams(window.location.search);
    if (
      !travellerDetails?.length ||
      queryParams.get("isUpcomingTrip") === "true"
    ) {
      if (
        queryParams.get("isUpcomingTrip") === "true" ||
        savedTravellers?.length === 0
      ) {
        upcomingTravellerDetails?.map((data: any) => {
          travellerDetail.push({
            ...data,
            dob: parseInt(data.dob),
            gender:
              data.gender.charAt(0).toUpperCase() +
              data.gender.slice(1).toLowerCase(),
            isSelected: queryParams.get("isUpcomingTrip") === "true",
          });
        });
      }
      savedTravellers?.map((data: any) => {
        travellerDetail.push({
          ...data,
          gender:
            data.gender.charAt(0).toUpperCase() +
            data.gender.slice(1).toLowerCase(),
          isSelected: data?.isDefaultSelected || false,
          isSavedTraveller: true,
          isEdited: false,
        });
      });
      setTravDetailsInMemory({
        ...travellerDataFromStore,
        travellerDetails: travellerDetail,
        primaryContactDetails: primaryContact,
        gstInfoDetails: gstInfo,
      });
      if (travellerDetail.length === 0) {
        setAddTrav(0);
      }
    } else {
      const selectedTravellersInMemory = travellerDetails?.filter(
        (data: ISecondaryContactV2) => data.isSelected
      );
      const remainingTravellersInMemory = travellerDetails?.filter(
        (data: ISecondaryContactV2) => !data.isSelected
      );
      const queryParams = new URLSearchParams(window.location.search);
      const travellers: number = Number(queryParams.get("travellers"));
      travellerDetail.push(
        ...selectedTravellersInMemory.slice(0, travellers),
        ...selectedTravellersInMemory
          .slice(travellers)
          .map((data: ISecondaryContactV2) => {
            return { ...data, isSelected: false };
          }),
        ...remainingTravellersInMemory
      );
    }
    let length = travellerDetail?.length
      ? travellerDetail?.length
      : upcomingTravellerDetails?.length
      ? upcomingTravellerDetails?.length
      : 1;
    setValue(
      "travellerData",
      Array.from({
        length: length,
      }).map((_, index) => ({
        name: travellerDetail?.[index]?.name || "",
        dob: travellerDetail?.[index]?.isSavedTraveller
          ? travellerDetail?.[index]?.dob || ""
          : travellerDetail?.[index]?.dob || "yyyy-mm-dd",
        gender: travellerDetail?.[index]?.gender || "",
        passport: travellerDetail?.[index]?.passport || "",
        nomineeName: travellerDetail?.[index]?.nomineeName || "",
        isSelected: travellerDetail?.[index]?.isSelected ?? true,
        isEdited: travellerDetail?.[index]?.isEdited ?? true,
        isSavedTraveller: travellerDetail?.[index]?.isSavedTraveller ?? false,
      }))
    );
  }, [secondaryContact, savedTravellers, upcomingTravellerDetails]);

  useEffect(() => {
    setValue("primaryContactData", {
      mobile:
        primaryContactDetails?.mobile ||
        primaryContact?.fields?.mobile?.value ||
        "",
      email:
        primaryContactDetails?.email ||
        primaryContact?.fields?.email?.value ||
        "",
    });
  }, [primaryContact]);

  useEffect(() => {
    setValue("gstInfoDetails", {
      gstNumber:
        gstInfoDetails?.gstNumber || gstInfo?.fields?.gstNumber?.value || "",
      companyDetails:
        gstInfoDetails?.companyDetails ||
        gstInfo?.fields?.companyDetails?.value ||
        "",
    });
  }, [gstInfo]);

  const selectedCountry = useMemo(() => {
    return countryData?.find((item) => item.isSelected);
  }, [countryData]);

  const handleArrowButton = (index: number) => {
    if (addTrav === index) {
      setAddTrav(-1);
      if (travellerData?.[index].isSelected) {
        trigger(`travellerData.${index}`);
      }
    } else {
      setAddTrav(index);
    }
  };

  const handleGenderChange = (data: IGender, index: number) => {
    setValue(`travellerData.${index}.gender`, data.label);
  };

  const saveFormValuesOnBlur = (index: number = -1) => {
    if (index != -1 && !getValues(`travellerData.${index}.isEdited`)) {
      setValue(`travellerData.${index}.isEdited`, true);
    }
    const { travellerData, primaryContactData, gstInfoDetails } = formValues;
    setTravDetailsInMemory({
      ...travellerDataFromStore,
      travellerDetails: travellerData,
      primaryContactDetails: primaryContactData,
      gstInfoDetails: gstInfoDetails,
    });
  };

  const handleTncCheckbox = (index: number) => {
    const value = formValues.tncs[index];
    setValue(`tncs.${index}`, !value);
    trigger(`tncs.${index}`);
  };

  const handleErrorToast = () => {
    setTimeout(() => {
      setIsSelectedError(false);
      setIsFieldError(false);
    }, 3000);
  };

  const scrollToTravellerWithPartiallyVisible = (index: number) => {
    const element = travellerRefMap.current?.[index];
    if (element) {
      const elementRect = element.getBoundingClientRect();
      const windowHeight =
        window.innerHeight || document.documentElement.clientHeight;
      // Check if element is partially visible
      const isPartiallyVisible = elementRect.bottom > windowHeight - 200;
      if (isPartiallyVisible) {
        const scrollTarget = window.pageYOffset + 200;
        window.scrollTo({
          top: scrollTarget,
          behavior: "smooth",
        });
      }
    }
  };

  const scrollToTravellerWithOffset = (index: number) => {
    const element = travellerRefMap.current?.[index];
    if (element) {
      const elementRect = element.getBoundingClientRect();
      const offsetTop = elementRect.top + window.pageYOffset - 100;

      window.scrollTo({
        top: offsetTop,
        behavior: "smooth",
      });
    }
  };

  const handleTravellerSelect = (value: boolean, index: number) => {
    if (getValues(`travellerData.${index}.isSelected`)) {
      setAddTrav(-1);
      setValue(`travellerData.${index}.isSelected`, false);
    } else {
      if (selectedCount === travellerCount) {
        if (!isSelectedError) {
          setIsSelectedError(true);
          handleErrorToast();
        }
      } else {
        setAddTrav(index);
        setValue(`travellerData.${index}.isSelected`, true);
        scrollToTravellerWithPartiallyVisible(index);
      }
    }
  };

  const onSubmit: SubmitHandler<ITravellerDetailsFormV2> = async (formData) => {
    if (onFormDataChange) {
      onFormDataChange(formData);
    }
    if (getSelectedCountry) {
      getSelectedCountry(selectedCountry);
    }
    analytics?.trackTravDetailsEvents(
      { actionType: "traveller_name_filled" },
      "pageView",
      type
    );
    analytics?.trackTravDetailsEvents(
      { actionType: "contact_details_filled" },
      "pageView",
      type
    );
    analytics?.trackTravDetailsEvents(
      { actionType: "dob_selected" },
      "pageView",
      type
    );
    analytics?.trackTravDetailsEvents(
      { actionType: "gender_selected" },
      "pageView",
      type
    );

    trackComponentView({
      name: "traveller_name_filled",
      type: "submit",
    });
    trackComponentView({
      name: "contact_details_filled",
      type: "submit",
    });
    trackComponentView({
      name: "dob_selected",
      type: "submit",
    });
    trackComponentView({
      name: "gender_selected",
      type: "submit",
    });

    const partner = getPartnerOnClientSide();
    let shouldLogin = false;
    const countryCode = selectedCountry?.countryCode
      ? selectedCountry.countryCode.replace(/^\+/, "")
      : "";
    if (!getCookieByName("mmt-auth") && !getCookieByName("OAUTH-GOIBIBO")) {
      shouldLogin = true;
      setIsEligibilityLoading(true);
      const eligibilityResponse = await checkCheckoutEligibility({
        partner,
        query: {
          itineraryId:
            new URLSearchParams(window.location.search).get("itineraryId") ||
            "",
        },
        phone: countryCode + formData?.primaryContactData?.mobile || "",
        email: formData?.primaryContactData?.email || "",
      });
      if (eligibilityResponse?.responseStatus === RESPONSE_STATUS.SUCCESS) {
        shouldLogin = eligibilityResponse?.data?.isLoginRequired;
      }
      setIsEligibilityLoading(false);
    }
    // triggerPageExit();
    if (shouldLogin) {
      setIsLoading(false);
      if (device === "sm" && partner === PARTNER.MMT) {
        setShowCheckoutLoginLoaderText(
          "Redirecting you to login to complete your purchase"
        );
        setTimeout(() => {
          sessionStorage.setItem("formData", JSON.stringify(formData));
          const req = {
            url: window.location.pathname + window.location.search,
          };
          document.cookie =
            "loginActionDetails=" +
            JSON.stringify(req.url) +
            ";domain=.makemytrip.com;path=/";
          window.location.href = "https://www.makemytrip.com/pwa/hlp/v3/login";
        }, 3000);
      } else {
        setActiveModal("LOGIN");
      }
    } else {
      storePageTransitionStart("traveller_details");
      await handleTravellerPayment?.(formData);
      return;
    }
  };

  const handleAddTraveller = () => {
    if (selectedCount != travellerCount) {
      setAddTrav(0);
      prepend({
        name: "",
        dob: "yyyy-mm-dd",
        gender: "",
        passport: "",
        nomineeName: "",
        isSelected: true,
      });
      setTimeout(() => {
        scrollToTravellerWithOffset(0);
      }, 100);
    }
  };

  const handleXDMErrorTrack = (errors: {
    travellerData?: any;
    primaryContactData?: any;
    tncs?: any;
    gstInfoDetails?: any;
  }) => {
    const errorXDMArr: any[] = [];

    // Process tncs errors
    if (errors.tncs && Array.isArray(errors.tncs)) {
      errors.tncs.forEach((error: any) => {
        errorXDMArr.push({
          message: error.message,
          source: error.ref?.name || "tncs",
          severity: "P3",
        });
      });
    }

    // Process travellerData errors
    if (errors.travellerData && Array.isArray(errors.travellerData)) {
      errors.travellerData.forEach((travellerError: any, index: number) => {
        Object.keys(travellerError).forEach((fieldKey: string) => {
          const fieldError = travellerError[fieldKey];
          errorXDMArr.push({
            message: fieldError.message,
            source: `travellerData.${index + 1}.${fieldKey}`,
            severity: "P3",
          });
        });
      });
    }

    // Process primaryContactData errors
    if (
      errors.primaryContactData &&
      typeof errors.primaryContactData === "object"
    ) {
      Object.keys(errors.primaryContactData).forEach((fieldKey: string) => {
        const fieldError = errors.primaryContactData[fieldKey];
        errorXDMArr.push({
          message: fieldError.message,
          source: fieldError.ref?.name || `primaryContactData.${fieldKey}`,
          severity: "P3",
        });
      });
    }

    // Process gstInfoDetails errors (if any)
    if (errors.gstInfoDetails && typeof errors.gstInfoDetails === "object") {
      Object.keys(errors.gstInfoDetails).forEach((fieldKey: string) => {
        const fieldError = errors.gstInfoDetails[fieldKey];
        if (fieldError && fieldError.message) {
          errorXDMArr.push({
            message: fieldError.message,
            source: fieldError.ref?.name || `gstInfoDetails.${fieldKey}`,
            severity: "P3",
          });
        }
      });
    }

    if (errorXDMArr.length > 0) {
      errorTrackEvent({
        error_details_list: errorXDMArr,
      });
    }
  };

  const onSubmitError: SubmitErrorHandler<ITravellerDetailsFormV2> = (
    errors
  ) => {
    if (!isFieldError) {
      setIsFieldError(true);
      handleErrorToast();
    }
    handleXDMErrorTrack(errors);
    if (errors.travellerData?.length) {
      const errorIndex = errors.travellerData?.findIndex?.((error) => error);
      if (errorIndex != null && errorIndex > -1) {
        setFirstErrorIndex(errorIndex);
      }
    } else if (
      errors.primaryContactData?.email ||
      errors.primaryContactData?.mobile
    ) {
      primaryContactRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "end",
        inline: "center",
      });
    } else if (errors.tncs?.length) {
      tncRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
        inline: "center",
      });
    } else if (
      errors.gstInfoDetails?.gstNumber ||
      errors.gstInfoDetails?.companyDetails
    ) {
      gstInfoRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "end",
        inline: "center",
      });
    }
  };

  useEffect(() => {
    if (firstErrorIndex > -1) {
      travellerRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
        inline: "nearest",
      });
      setAddTrav(-1);
    }
    setFirstErrorIndex(-1);
  }, [firstErrorIndex]);

  type FormFieldType = "name" | "dob" | "gender" | "passport" | "nomineeName";

  const filterTravellers = async (
    selected: number[],
    errors: TravellerError[]
  ) => {
    let targetPosition = 0;
    const newIndices: any = new Array(travellers.length);
    const errorIndices = errors.map((err) => err.index);

    travellers.forEach((traveller, index) => {
      if (errorIndices.includes(index)) {
        newIndices[index] = targetPosition;
        targetPosition++;
      }
    });

    travellers.forEach((traveller, index) => {
      if (selected.includes(index) && !errorIndices.includes(index)) {
        newIndices[index] = targetPosition;
        targetPosition++;
      }
    });

    travellers.forEach((traveller, index) => {
      if (!selected.includes(index) && !errorIndices.includes(index)) {
        newIndices[index] = targetPosition;
        targetPosition++;
      }
    });

    const newArray: any = [];
    newIndices.forEach((indexVal: number, index: number) => {
      newArray[indexVal] = getValues(`travellerData.${index}`);
    });
    replace(newArray);
    const isValid = await trigger("travellerData");

    errors.forEach(({ index, errorType }, ind) => {
      const fieldKey = `travellerData.${ind}.${errorType}` as
        | `travellerData.${number}.name`
        | `travellerData.${number}.dob`
        | `travellerData.${number}.gender`
        | `travellerData.${number}.passport`
        | `travellerData.${number}.nomineeName`;

      setError(fieldKey, {
        message: getFormFieldErrorMsg(errorType as FormFieldType),
      });
    });
  };

  const handleBuy = (fromBenefitModal: boolean = false) => {
    if (externalIsLoading || isEligibilityLoading) {
      return;
    }
    if (showBenefits) {
      handleViewBenefits();
    }
    if (selectedCount !== travellerCount) {
      if (!isFieldError) {
        setIsFieldError(true);
        errorTrackEvent({
          error_details_list: [
            {
              message: "Please Select Traveller",
              source: "traveller_select",
              severity: "P3",
            },
          ],
        });
        setFirstErrorIndex(0);
        handleErrorToast();
      }
    } else {
      if (isDomesticBooking) {
        if (fromBenefitModal) {
          analytics?.trackTravDetailsEvents(
            { actionType: "buy_clicked_details_domestic" },
            "pageView",
            type
          );
          trackEvent({
            event_detail: {
              event_type: "action",
              event_name: "component_cta-clicked",
              event_value: "buy_clicked_details_domestic",
            },
          });
        } else {
          trackEvent({
            event_detail: {
              event_type: "action",
              event_name: "component_cta-clicked",
              event_value: "buy_clicked_outside_domestic",
            },
          });
          analytics?.trackTravDetailsEvents(
            { actionType: "buy_clicked_outside_domestic" },
            "pageView",
            type
          );
        }
      } else {
        trackEvent({
          event_detail: {
            event_type: "action",
            event_name: "component_cta-clicked",
            event_value: "buy_clicked",
          },
        });

        analytics?.trackTravDetailsEvents(
          { actionType: "buy_clicked" },
          "pageView",
          type
        );
      }
      const formValues = getValues();
      const selected: number[] = [];
      const errorArr: TravellerError[] = [];
      // const errorXDMArr: any[] = [];

      formValues?.travellerData?.map((item, index) => {
        const {
          name: fullName,
          dob: dobValue,
          gender: genderType,
          passport: passportType,
          nomineeName: nomineeNameType,
          isSelected,
        } = item || {};

        if (isSelected) {
          selected.push(index);
        }
        const fieldState = getFieldState(`travellerData.${index}`);
        const { isTouched } = fieldState;

        if (!isTouched) {
          if (name && name?.isRequired && !fullName) {
            if (selected.includes(index)) {
              errorArr.push({
                index,
                errorType: "name",
              });
            }
          } else if (dob && dob?.isRequired && !dobValue) {
            if (selected.includes(index)) {
              errorArr.push({
                index,
                errorType: "dob",
              });
            }
          } else if (gender && gender?.isRequired && !genderType) {
            if (selected.includes(index)) {
              errorArr.push({
                index,
                errorType: "gender",
              });
            }
          } else if (passport && passport?.isRequired && !passportType) {
            if (selected.includes(index)) {
              errorArr.push({
                index,
                errorType: "passport",
              });
            }
          } else if (
            nomineeName &&
            nomineeName?.isRequired &&
            !nomineeNameType
          ) {
            if (selected.includes(index)) {
              errorArr.push({
                index,
                errorType: "nomineeName",
              });
            }
          }
        }
      });
      filterTravellers(selected, errorArr);
      handleSubmit(onSubmit, onSubmitError)();
    }
  };

  if (checkoutError && typeof window !== "undefined") {
    throw new ClientError(checkoutError);
  }

  const handleAddOnFilters = (selectedFilterId?: number | string) => {
    analytics?.trackTravDetailsEvents(
      { actionType: "Filter_selected" },
      "pageView",
      type
    );
    trackEvent({
      event_detail: {
        event_type: "action",
        event_name: "component_cta-clicked",
        event_value: "Filter_selected",
      },
    });
    const filterData = filters?.map((filter: IFilters) => {
      if (filter.id === selectedFilterId) {
        filter.isSelected = true;
      } else {
        filter.isSelected = false;
      }
      return filter;
    });
    const selectedFilteredPlans = filterData?.filter(
      (filter: IFilters) => filter.isSelected
    )?.[0]?.plans;
    let currentAddOnPlans = addOnPlans
      ?.map((plan: IAddOnPlans) => {
        if (selectedFilteredPlans?.includes(plan?.planCode)) {
          return plan;
        } else {
          return null;
        }
      })
      ?.filter((plan: any) => plan !== null);
    setAddOnPlansData(currentAddOnPlans);
    setAddOnFilters(filterData);
  };

  const handleAddOnSelect = (planCode: string) => {
    const selectedFilteredPlans = addOnFilters?.filter(
      (filter: IFilters) => filter.isSelected
    )?.[0]?.plans;

    const addOnData = addOnPlans
      ?.map((plan: IAddOnPlans, index: number) => {
        if (plan?.planCode === planCode) {
          if (plan.isSelected) {
            const queryParams = new URLSearchParams(window.location.search);
            const quoteId = queryParams.get("quoteId");
            setQuoteId(quoteId);
            plan.isSelected = false;
          } else {
            setQuoteId(plan?.quoteId);
            plan.isSelected = true;
          }
        } else {
          plan.isSelected = false;
        }
        return plan;
      })
      ?.filter((plan: IAddOnPlans) =>
        selectedFilteredPlans?.includes(plan?.planCode)
      );

    const selectedAddOnProdDetails = addOnPlans?.filter(
      (plan: IAddOnPlans) => plan?.isSelected
    )?.[0]?.comboProductDetails;
    if (selectedAddOnProdDetails) {
      setAddOnProductDetails?.(selectedAddOnProdDetails);
    } else {
      setAddOnProductDetails?.(null);
    }
    setAddOnPlansData(addOnData);
  };

  const handleViewTraveller = () => {
    if (showAllTravellers) {
      analytics?.trackTravDetailsEvents(
        { actionType: "viewLessButton_clicked" },
        "pageView",
        type
      );
      trackEvent({
        event_detail: {
          event_type: "action",
          event_name: "component_cta-clicked",
          event_value: "viewLessButton_clicked",
        },
      });
    } else {
      analytics?.trackTravDetailsEvents(
        { actionType: "viewAllButton_clicked" },
        "pageView",
        type
      );
      trackEvent({
        event_detail: {
          event_type: "action",
          event_name: "component_cta-clicked",
          event_value: "viewAllButton_clicked",
        },
      });
    }
    setShowAllTravellers(!showAllTravellers);
  };

  const getVisibleTravellers = () => {
    if (isSavedTravellerPokusEnabled) {
      return showAllTravellers ? travellers : travellers.slice(0, 5);
    }
    return travellers;
  };

  useEffect(() => {
    if (onSetError) {
      setError(onSetError?.errTrype, onSetError?.errName);
    }
  }, [onSetError, setError]);

  return (
    <FormProvider {...methods}>
      <AddTravellerDetailStyle>
        <AddTravellerHd>
          <H5Tag>
            <FormattedText value={title} />
          </H5Tag>
          <ParaTag fontSize="xs" color={theme.greyText}>
            <FormattedText value={info} />
          </ParaTag>
        </AddTravellerHd>

        {getVisibleTravellers().map(
          (
            traveller: FieldArrayWithId<
              ITravellerDetailsFormV2,
              "travellerData",
              "id"
            >,
            index: number
          ) => {
            const { gender, isSelected } =
              formValues?.travellerData?.[index] || {};
            return (
              <div
                key={traveller.id}
                ref={(el) => (travellerRefMap.current[index] = el)}
              >
                <TravellerDetailsV2
                  type={type}
                  handleTravellerSelect={handleTravellerSelect}
                  travellerIndex={index}
                  travellerRef={travellerRef}
                  isLoading={isLoading}
                  firstErrorIndex={firstErrorIndex}
                  travellerSelected={addTrav == index}
                  isSelected={isSelected}
                  count={selectedCount}
                  travellerNumber={`${secondaryContact?.title} ${index + 1}`}
                  isSavedTravellers={savedTravellers?.length ? true : false}
                  secondaryContactData={secondaryContact?.fields}
                  genderTypes={
                    secondaryContact?.fields?.gender?.values || GENDER_VALUES
                  }
                  selectedGender={gender}
                  handleGenderChange={(item) => handleGenderChange(item, index)}
                  handleArrowButton={() => handleArrowButton(index)}
                  travellerCount={travellerCount}
                  saveFormValuesOnBlur={saveFormValuesOnBlur}
                  isDomesticBooking={isDomesticBooking}
                  isValidForm={isValidForm}
                  isEditable={
                    secondaryContact?.isEditable === false ? false : true
                  }
                  partner={partner}
                />
              </div>
            );
          }
        )}

        <SectionDividers height="1.2px" />
        {!isDomesticBooking && (
          <AddViewButton>
            <AddButtonWrap
              isDisabled={selectedCount === travellerCount}
              onClick={handleAddTraveller}
            >
              <ImageComponent
                width="16px"
                height="16px"
                margin="0px 10px 0px 0px"
                src={
                  selectedCount === travellerCount
                    ? "https://tripmoneycmsimgak.mmtcdn.com/img/Vector_529ac663fb.svg"
                    : partner === PARTNER.MYBIZ
                    ? "https://tripmoneycmsimgak.mmtcdn.com/img/Vector_3e57c47aa3.svg"
                    : "https://tripmoneycmsimgak.mmtcdn.com/img/Vector_68e1489f3a.svg"
                }
              />

              <FormattedText
                value={"ADD ANOTHER TRAVELLER"}
                baseStyle={{
                  fontWeight: "800",
                }}
              />
            </AddButtonWrap>

            {isSavedTravellerPokusEnabled && travellers.length > 5 && (
              <ViewButtonWrap onClick={handleViewTraveller}>
                <FormattedText
                  value={showAllTravellers ? "VIEW LESS" : "VIEW ALL"}
                  baseStyle={{
                    fontWeight: "800",
                  }}
                />

                <ArrowIcon
                  isRotated={showAllTravellers}
                  src={
                    partner === PARTNER.MYBIZ
                      ? "https://tripmoneycmsimgak.mmtcdn.com/img/Arrow_Down_Blue_2e09571187.svg"
                      : "https://tripmoneycmsimgak.mmtcdn.com/img/Arrow_Down_Blue_5d58cf6783.svg"
                  }
                  alt="View toggle icon"
                />
              </ViewButtonWrap>
            )}
          </AddViewButton>
        )}
        <SectionDividers small />

        {addOns && (
          <TravellerCommonComp
            title={addOns?.title}
            info={addOns?.subTitle}
            addOnPlans={addOnPlansData}
            addOnfilters={addOnFilters}
            handleBuy={handleBuy}
            handleAddOnFilters={handleAddOnFilters}
            handleAddOnSelect={handleAddOnSelect}
          ></TravellerCommonComp>
        )}

        {addOns && <SectionDividers small />}

        <TravellerCommonComp
          componentRef={primaryContactRef}
          title={primaryContact?.title}
          info={primaryContact?.info}
          note={isDomesticBooking ? undefined : note}
          primaryContactDetails
          primaryContactData={primaryContact?.fields}
          saveFormValuesOnBlur={saveFormValuesOnBlur}
          countryData={countryData}
          selectedCountry={selectedCountry}
          handleCountryCodeModal={handleCountryCodeModal}
        />
        {gstInfo && <SectionDividers small />}

        {gstInfo && (
          <TravellerCommonComp
            componentRef={gstInfoRef}
            title={gstInfo?.title}
            gstInfoData={gstInfo?.fields}
            saveFormValuesOnBlur={saveFormValuesOnBlur}
          />
        )}
        <SectionDividers small />

        {/* NOT IN CURRENT SCOPE : Choose from multiple insurers*/}
        {/* <TravellerCommonComp
                title="Select Insurer"
                info="Select from the available Insurers to secure your trip."
                selectInsurer={selectInsurer}
            />
            <SectionDividers small /> */}

        <TravellerCommonComp
          componentRef={tncRef}
          confirmTnC={concent?.tnc}
          handleTncCheckbox={handleTncCheckbox}
          info={isDomesticBooking ? "" : concent?.info}
          totalAmt={
            addOnProductDetails?.totalAmountToPay
              ? addOnProductDetails?.totalAmountToPay
              : totalAmt
          }
          handleBuy={() => {
            handleBuy();
          }}
          isLoading={isLoading}
        />
      </AddTravellerDetailStyle>
      {isSelectedError && (
        <WebToast
          toastTxt={`Already selected ${travellerCount} ${
            travellerCount === 1 ? "traveller" : "travellers"
          }, Remove before adding one.`}
          position="fixed"
          right={"50px"}
          margin={device === "lg" ? "0px 0px 0px 28px" : ""}
          height="40px"
          bottom={device === "sm" ? "70px" : "50px"}
        />
      )}
      {isFieldError && (
        <WebToastWrap>
          <WebToast
            type="error"
            toastTxt={
              selectedCount === travellerCount
                ? widgets?.formErrorToast
                  ? widgets?.formErrorToast?.message
                  : formErrorToast
                : widgets?.addErrorToast
                ? widgets?.addErrorToast?.message
                : addErrorToast
            }
            color="#6B4300"
          />
        </WebToastWrap>
      )}

      {widgets?.offerToast && showOfferToast && (
        <WebToast
          type="success"
          iconUrl={widgets?.offerToast?.iconUrl}
          toastTxt={widgets?.offerToast?.message}
          position="fixed"
          toastTxtMargin="0px 0px 0px 5px"
          color="#1E5C43"
          fontSize="14px"
          fontWeight="700"
          right="50px"
          bottom={device === "sm" ? "70px" : "50px"}
        />
      )}

      {showBenefits && (
        <MoreBenefits
          data={benefitsData}
          moreBenfitsTitle={benefitsTitle}
          handleClose={handleViewBenefits}
          handleBuyButton={() => {
            handleBuy(true);
          }}
        />
      )}
      {showCheckoutLoginLoaderText && (
        <CheckoutLoaderBackdrop>
          <CheckoutLoader />
          <CheckoutLoaderText>{showCheckoutLoginLoaderText}</CheckoutLoaderText>
        </CheckoutLoaderBackdrop>
      )}
    </FormProvider>
  );
};

export default AddTravellerDetails;
